"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const ASCII_PRINTABLE_START = 0x21;
const ASCII_PRINTABLE_END = 0x7e;
const FULLWIDTH_OFFSET = 0xfe_e0;
const IDEOGRAPHIC_SPACE = "　";

function toWideChar(char: string): string {
  if (char === " ") {
    return IDEOGRAPHIC_SPACE;
  }
  const code = char.codePointAt(0);
  if (
    code !== undefined &&
    code >= ASCII_PRINTABLE_START &&
    code <= ASCII_PRINTABLE_END
  ) {
    return String.fromCodePoint(code + FULLWIDTH_OFFSET);
  }
  return char;
}

function toWideText(text: string): string {
  return [...text].map(toWideChar).join("");
}

/** English strings. A translated content file overrides these through `ui`. */
export const WIDE_TEXT_GENERATOR_STRINGS = {
  yourText: "Your text",
  inputPlaceholder: "Type or paste text to widen",
  wideTextResult: "Wide text (full-width)",
  copied: "Copied!",
  copy: "Copy",
  placeholderResult: "Your result will appear here",
} as const;

export default function WideTextGeneratorWidget() {
  const strings = useToolUi(WIDE_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const [copied, setCopied] = useState(false);

  const wideText = toWideText(input);

  function handleCopy() {
    if (wideText.length === 0) {
      return;
    }
    writeToClipboard(wideText).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="wide-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="wide-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
      </div>

      {/* Result */}
      <div className="space-y-2 rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="font-medium text-foreground text-sm">
            {strings.wideTextResult}
          </p>
          <button
            className="rounded-lg border border-border bg-background px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
            disabled={input.length === 0}
            onClick={handleCopy}
            type="button"
          >
            {copied ? strings.copied : strings.copy}
          </button>
        </div>
        <p className="min-h-[2rem] whitespace-pre-wrap break-words text-base text-foreground leading-relaxed">
          {input.length > 0 ? wideText : strings.placeholderResult}
        </p>
      </div>
    </>
  );
}
